import { API_CONFIG } from "../../config/api_config";
import { EditorTree, TreeListItem } from "./../types/types";

import MockComputationService from "./backendService/mock/mockComputationService";
import MockTreeService from "./backendService/mock/mockTreeService";
import MockUserService from "./backendService/mock/mockUserService";

import RealComputationService from "./backendService/real/realComputationService";
import RealTreeService from "./backendService/real/realTreeService";
import RealUserService from "./backendService/real/realUserService";

/*
This is the main service that will be used to interact with the backend API.

It acts as both a factory and a service, creating instances of the various services (tree, user, computation)
and providing a unified interface for the frontend to interact with.

The service will use the environment configuration to determine whether to use mock or real implementations.
The API base URL and whether to use mock APIs are determined by the environment configuration.

*/

function getTokenFromStorage(key: string) {
  try {
    // Prefer exact match first
    const exact = localStorage.getItem(key);
    if (exact !== null) return exact;

    // Otherwise find any localStorage key that ends with the provided key
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.endsWith(key)) {
        return localStorage.getItem(k);
      }
    }
  } catch (e) {
    console.warn("getTokenFromStorage error:", e);
  }
  return null;
}

class BackendApiService {
  // Function to get the authentication token
  private getToken: () => string | null;

  // API base URL - would point to your actual backend when implemented
  private baseUrl: string = API_CONFIG().BASE_URL;
  private mockDelay: number = parseInt(API_CONFIG().MOCK_API_DELAY, 10);
  // Factory to select between mock and real implementations
  private useMock = API_CONFIG().USE_MOCK_API;

  private treeService: MockTreeService | RealTreeService;
  private userService: MockUserService | RealUserService;
  private computationService: MockComputationService | RealComputationService;

  constructor(
    getToken: () => string | null = () => getTokenFromStorage("idToken"),
  ) {
    this.getToken = getToken;
    this.treeService = this.useMock
      ? new MockTreeService(this.mockDelay)
      : new RealTreeService(this.baseUrl, this.getToken);
    this.userService = this.useMock
      ? new MockUserService(this.mockDelay)
      : new RealUserService(this.baseUrl, this.getToken);
    this.computationService = this.useMock
      ? new MockComputationService(this.mockDelay)
      : new RealComputationService(this.baseUrl, this.getToken);
  }

  async fetchUserProfile() {
    return this.userService.fetchUserProfile();
  }
  async updateUserProfile(profile: any) {
    return this.userService.updateUserProfile(profile);
  }
  async updateUserEmail(newEmail: string) {
    return this.userService.updateUserEmail
      ? this.userService.updateUserEmail(newEmail)
      : Promise.reject(new Error("updateUserEmail not implemented"));
  }
  async changePassword(currentPassword: string, newPassword: string) {
    return this.userService.changePassword
      ? this.userService.changePassword(currentPassword, newPassword)
      : Promise.reject(new Error("changePassword not implemented"));
  }

  // Tree methods
  async fetchTreeList(): Promise<TreeListItem[]> {
    return this.treeService.fetchTreeList();
  }
  async fetchTree(id: string): Promise<EditorTree> {
    return this.treeService.fetchTree(id);
  }
  async saveTree(tree: EditorTree): Promise<EditorTree> {
    return this.treeService.saveTree(tree);
  }
  async deleteTree(id: string): Promise<void> {
    return this.treeService.deleteTree(id);
  }

  // Computation methods
  async calculateTree(tree: EditorTree): Promise<EditorTree> {
    return this.computationService.calculateTree(tree);
  }
  async runSensitivityAnalysis(treeId: string, params: any): Promise<any> {
    return this.computationService.runSensitivityAnalysis(treeId, params);
  }
  async runSimulation(treeId: string, settings: any): Promise<any> {
    return this.computationService.runSimulation(treeId, settings);
  }
  async generateInfluenceDiagram(treeId: string): Promise<any> {
    return this.computationService.generateInfluenceDiagram(treeId);
  }

  /**
   * Generate AI summary of decision tree
   * @param {object} params - AI summary parameters
   * @param {object} params.tree - Decision tree data
   * @param {object} params.tree_config - Tree configuration
   * @param {Array} params.chat_history - Chat history for context
   * @param {string} params.user_prompt - User's analysis request
   * @returns {Promise<any>} AI summary results
   */
  async generateAISummary(params: any) {
    return this.computationService.generateAISummary(params);
  }

  // Subscription / billing related helper methods
  async createCheckoutSession(
    planType: string | undefined | null,
    userEmail: string,
  ) {
    return this.userService.createCheckoutSession
      ? this.userService.createCheckoutSession(
          planType ?? "monthly_professional",
          userEmail,
        )
      : Promise.reject(new Error("createCheckoutSession not implemented"));
  }

  async createCheckoutSessionWithOptions(
    planType: string | undefined | null,
    payload: {
      userEmail: string;
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ) {
    return this.userService.createCheckoutSessionWithOptions
      ? this.userService.createCheckoutSessionWithOptions(
          planType ?? "monthly_professional",
          payload,
        )
      : Promise.reject(
          new Error("createCheckoutSessionWithOptions not implemented"),
        );
  }

  async createSubscriptionCheckout(
    tierId: string,
    payload?: {
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ) {
    return this.userService.createSubscriptionCheckout
      ? this.userService.createSubscriptionCheckout(tierId, payload)
      : Promise.reject(new Error("createSubscriptionCheckout not implemented"));
  }

  async getSubscriptionStatus() {
    return this.userService.getSubscriptionStatus
      ? this.userService.getSubscriptionStatus()
      : Promise.resolve({ status: "none", has_active_subscription: false });
  }

  async createPortalSession() {
    return this.userService.createPortalSession
      ? this.userService.createPortalSession()
      : Promise.reject(new Error("createPortalSession not implemented"));
  }

  async getHasStripeCustomer() {
    return this.userService.getHasStripeCustomer
      ? this.userService.getHasStripeCustomer()
      : Promise.resolve({ has_customer: false });
  }

  // Alternate public names expected by AuthContext
  async fetchSubscriptionStatus() {
    return this.getSubscriptionStatus();
  }

  async createSubscriptionPortal() {
    return this.createPortalSession();
  }

  async verifySubscription() {
    return this.userService.verifySubscription
      ? this.userService.verifySubscription()
      : Promise.resolve({
          verified: false,
          status: "none",
          source: "none",
          has_active_subscription: false,
        });
  }
}

export const backendApiService = new BackendApiService();
